import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Card } from '../components/UI';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { motion } from 'framer-motion';
import { ThemeAwareHero } from '../components/hero';
import { ThemeAwareFeatures } from '../components/features';
import { ThemeAwareBenefits } from '../components/benefits';
import { ThemeAwareCTA } from '../components/cta';
import { HKDTooltipBubble } from '../components/HKDTooltipBubble';
import { HospitalityReel } from '../components/hospitality/HospitalityReel';
import { RestoPhotoMarquee } from '../components/hospitality/RestoPhotoMarquee';

export const Home: React.FC = () => {
  const { t, i18n } = useTranslation(['common', 'home', 'compare']);
  const { theme, isBlack } = useTheme();
  const { currency } = useCurrency(); // Récupérer la devise actuelle
  const [showHKDTooltip, setShowHKDTooltip] = useState(false);

  // Afficher la bulle HKD après 3 secondes (pour utilisateurs chinois OU en devise CNY)
  useEffect(() => {
    // Vérifier si l'utilisateur est en chinois OU utilise la devise CNY
    const shouldShow = i18n.language === 'zh' || currency === 'CNY';
    if (!shouldShow) return;

    // Afficher après 3 secondes à CHAQUE chargement de page (plus de localStorage)
    const timer = setTimeout(() => {
      setShowHKDTooltip(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [i18n.language, currency]);

  const handleCloseTooltip = () => {
    setShowHKDTooltip(false);
    // Pas de localStorage - la bulle reviendra au prochain refresh
  };

  // REDIRECTION DÉSACTIVÉE TEMPORAIREMENT (problèmes sur mobile)
  // TODO: Implémenter redirection côté serveur Firebase Cloud Functions
  
  // Utiliser 'office' comme thème par défaut si le thème n'est pas défini
  const currentTheme = theme || 'office';

  // Récupérer les témoignages depuis home.json avec la nouvelle structure
  const testimonialsRaw = t('home:testimonials', { returnObjects: true });
  
  // Safety guard: si les traductions ne sont pas encore chargées, returnObjects renvoie une string
  const testimonialsObject = (typeof testimonialsRaw === 'object' && testimonialsRaw !== null) 
    ? testimonialsRaw as Record<string, any> 
    : {} as Record<string, any>;
  
  // Extraire tous les témoignages (uniquement ceux avec les champs attendus)
  const allTestimonials = Object.entries(testimonialsObject)
    .filter(([key]) => key.startsWith('office_') || key.startsWith('resto_') || key.startsWith('nightclub_'))
    .map(([, testimonial]) => testimonial)
    .filter((t: any) => t && typeof t === 'object' && t.content && t.name && t.type);

  // Filtrer les témoignages selon le thème actuel
  const filteredTestimonials = allTestimonials.filter(testimonial => 
    testimonial.type === currentTheme
  );

  // Afficher TOUS les témoignages filtrés (pas seulement 3)
  const testimonials = filteredTestimonials;

  return (
    <div className={`min-h-screen ${isBlack ? 'bg-transparent text-white' : 'bg-white dark:bg-transparent'}`}>
      {/* Bulle HKD pour Hong Kong (uniquement ZH) */}
      <HKDTooltipBubble 
        isVisible={showHKDTooltip} 
        onClose={handleCloseTooltip}
      />

      {theme === 'resto' && <HospitalityReel />}

      {/* Theme-aware Hero Section */}
      <ThemeAwareHero />

      {/* Theme-aware Features Section */}
      <ThemeAwareFeatures />

      {/* Theme-aware Benefits Section */}
      <ThemeAwareBenefits />

      {/* Testimonials Section (marquee infini, direction selon langue) */}
      <section className={`py-20 ${isBlack ? 'bg-transparent' : 'bg-gray-50 dark:bg-black/30'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t(`home:testimonials.${currentTheme}.title`)}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              {t(`home:testimonials.${currentTheme}.subtitle`)}
            </p>
          </div>
        </div>

        {theme === 'resto' && <RestoPhotoMarquee />}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Version Mobile & Tablette - Carrousel avec boutons */}
          <div className="block lg:hidden">
            {(() => {
              const [currentIndex, setCurrentIndex] = React.useState(0);
              const cards = filteredTestimonials.length ? filteredTestimonials : testimonials;
              const isDark = document.documentElement.classList.contains('dark');
              
              return (
                <div className="relative px-12">
                  <div className="overflow-hidden">
                    <div 
                      className="flex transition-transform duration-500 ease-in-out" 
                      style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                    >
                      {cards.map((testimonial: any, index: number) => (
                        <div key={index} className="w-full flex-shrink-0 px-4">
                          <Card className={`text-center ${isBlack ? 'black-card' : ''} max-w-md mx-auto`}>
                            <div className="flex justify-center mb-4">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                              ))}
                            </div>
                            <p className="text-gray-600 dark:text-gray-300 mb-4 italic">
                              "{testimonial.content}"
                            </p>
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {testimonial.name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {testimonial.role}
                              </p>
                            </div>
                          </Card>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Boutons navigation - Plus visibles */}
                  {cards.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentIndex((prev) => (prev - 1 + cards.length) % cards.length)}
                        className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full backdrop-blur-sm border-2 ${
                          isDark || isBlack
                            ? 'bg-gray-800/90 border-gray-700 hover:bg-gray-700' 
                            : 'bg-white/90 border-gray-300 hover:bg-gray-50'
                        } shadow-xl hover:scale-110 transition-all`}
                        aria-label="Avis précédent"
                      >
                        <ChevronLeft className={`h-6 w-6 ${isDark || isBlack ? 'text-yellow-500' : 'text-gray-900'}`} />
                      </button>
                      <button
                        onClick={() => setCurrentIndex((prev) => (prev + 1) % cards.length)}
                        className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full backdrop-blur-sm border-2 ${
                          isDark || isBlack
                            ? 'bg-gray-800/90 border-gray-700 hover:bg-gray-700' 
                            : 'bg-white/90 border-gray-300 hover:bg-gray-50'
                        } shadow-xl hover:scale-110 transition-all`}
                        aria-label="Avis suivant"
                      >
                        <ChevronRight className={`h-6 w-6 ${isDark || isBlack ? 'text-yellow-500' : 'text-gray-900'}`} />
                      </button>
                    </>
                  )}
                  
                  {/* Indicateurs (dots optimisés mobile) */}
                  {cards.length > 1 && (
                    <div className="flex justify-center gap-2 mt-6">
                      {cards.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentIndex(index)}
                          className={`h-1.5 rounded-full transition-all ${
                            currentIndex === index 
                              ? 'w-6 bg-yellow-500' 
                              : 'w-1.5 bg-gray-400 hover:bg-gray-500'
                          }`}
                          aria-label={`Aller à l'avis ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Version Desktop - Marquee infini avec Framer Motion */}
          <div className="hidden lg:block overflow-hidden">
            {(() => {
              const dir = typeof i18n.dir === 'function' ? i18n.dir(i18n.language) : 'ltr';
              const cards = testimonials.length ? testimonials : [];
              
              // Dupliquer 3 fois pour garantir le scroll fluide
              const duplicatedCards = [...cards, ...cards, ...cards];
              
              return (
                <div className="relative overflow-hidden w-full">
                  <motion.div 
                    className="flex gap-6 items-stretch"
                    animate={{
                      x: dir === 'rtl' ? [0, cards.length * (360 + 24)] : [0, -cards.length * (360 + 24)]
                    }}
                    transition={{
                      x: {
                        repeat: Infinity,
                        repeatType: "loop",
                        duration: 20,
                        ease: "linear",
                      },
                    }}
                  >
                    {duplicatedCards.map((testimonial: any, index: number) => (
                      <Card 
                        key={`card-${index}`} 
                        className={`text-center flex-shrink-0 ${isBlack ? 'black-card' : ''}`}
                        style={{ width: '360px', minWidth: '360px' }}
                      >
                        <div className="flex justify-center mb-4">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                          ))}
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 mb-4 italic line-clamp-3">
                          "{testimonial.content}"
                        </p>
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {testimonial.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {testimonial.role}
                          </p>
                        </div>
                      </Card>
                    ))}
                  </motion.div>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* Theme-aware CTA Section */}
      <ThemeAwareCTA />
    </div>
  );
};